import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { updateStrategicIssue, fetchStrategicIssues } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function StrategicIssueEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [form, setForm] = useState({ successFactorId: "", businessDomainId: "", successFactorName: "", successFactorDescription: "" });
  const [allIssues, setAllIssues] = useState([]);
  // Sources that are not strategic issues (from other categories) — preserved on save
  const [nonIssueSources, setNonIssueSources] = useState([]);
  const [selectedIssueSources, setSelectedIssueSources] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const issues = await fetchStrategicIssues(businessDomainId);
        const d = issues.find((i) => i.successFactorId === successFactorId) || {};
        const otherIssues = issues.filter((i) => i.successFactorId !== successFactorId);
        const allSources = d.sources || [];
        const issueIds = new Set(otherIssues.map((i) => i.successFactorId));
        // Split sources: those that are strategic issues vs. other types
        const issueSources = allSources.filter((s) => issueIds.has(s.successFactorId));
        const otherSources = allSources.filter((s) => !issueIds.has(s.successFactorId));
        setForm({
          successFactorId: d.successFactorId || "",
          businessDomainId: d.businessDomainId || businessDomainId,
          successFactorName: d.successFactorName || "",
          successFactorDescription: d.successFactorDescription || "",
          businessProcesses: d.businessProcesses || [],
          indicators: d.indicators || [],
          parentId: d.parentId || null,
        });
        setAllIssues(otherIssues);
        setNonIssueSources(otherSources);
        setSelectedIssueSources(issueSources);
      } catch (e) { notify.apiError(e); }
    })();
  }, [successFactorId, businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSourcesChange = (_, value) =>
    setSelectedIssueSources(value.map((v) => ({ successFactorId: v.successFactorId, successFactorName: v.successFactorName })));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Preserve non-strategic-issue sources; only replace strategic-issue sources
      const mergedSources = [...nonIssueSources, ...selectedIssueSources];
      await updateStrategicIssue({ ...form, sources: mergedSources });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/strategic-issues/${successFactorId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("strategicIssue.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label={t("successFactor.name")} name="successFactorName" value={form.successFactorName} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")} name="successFactorDescription" value={form.successFactorDescription} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={allIssues}
              getOptionLabel={(opt) => opt.successFactorName || ""}
              isOptionEqualToValue={(opt, val) => opt.successFactorId === val.successFactorId}
              value={allIssues.filter((i) => selectedIssueSources.some((s) => s.successFactorId === i.successFactorId))}
              onChange={handleSourcesChange}
              renderInput={(params) => (
                <TextField {...params} label={t("strategicIssue.sources.label")} />
              )}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/strategic-issues/${successFactorId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
